import { createSlice } from "@reduxjs/toolkit";
import {
    getAllServiceAreas,
    createServiceArea,
    updateServiceArea,
    deleteServiceArea,
    getServiceAreaById,
    getProvincesAndCities,
} from "../Actions/ServiceAreaAction";
import { ServiceArea } from "../../modules/admin/models/ServiceArea.model";

// Service Area State Interface
export interface ServiceAreaState {
    serviceAreas: {
        data: ServiceArea[];
        totalCount: number;
        hasNextPage: boolean;
    };
    selectedServiceArea: ServiceArea | null;
    provincesAndCities: {
        provinces: string[];
        cities: Record<string, string[]>;
    };
    loading: boolean;
    error: string | null;
}

// Initial State
const initialState: ServiceAreaState = {
    serviceAreas: {
        data: [],
        totalCount: 0,
        hasNextPage: false,
    },
    selectedServiceArea: null,
    provincesAndCities: {
        provinces: [],
        cities: {},
    },
    loading: false,
    error: null,
};

// Service Area Slice
const serviceAreaSlice = createSlice({
    name: "serviceArea",
    initialState,
    reducers: {
        // Clear selected service area
        clearSelectedServiceArea: (state) => {
            state.selectedServiceArea = null;
        },
        // Clear error
        clearError: (state) => {
            state.error = null;
        },
        // Set loading state
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get All Service Areas
            .addCase(getAllServiceAreas.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllServiceAreas.fulfilled, (state, action) => {
                state.loading = false;
                state.serviceAreas = {
                    data: action.payload.data || [],
                    totalCount: action.payload.totalCount || 0,
                    hasNextPage: action.payload.hasNextPage || false,
                };
                state.error = null;
            })
            .addCase(getAllServiceAreas.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to fetch service areas";
            })
            
            // Create Service Area
            .addCase(createServiceArea.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createServiceArea.fulfilled, (state, action) => {
                state.loading = false;
                // Add new service area to the first position
                if (action.payload) {
                    state.serviceAreas.data = [action.payload, ...state.serviceAreas.data];
                    state.serviceAreas.totalCount += 1;
                }
                state.error = null;
            })
            .addCase(createServiceArea.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to create service area";
            })
            
            // Update Service Area
            .addCase(updateServiceArea.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateServiceArea.fulfilled, (state, action) => {
                state.loading = false;
                // Find and update the specific service area
                const updated = action.payload;
                if (updated && updated.id) {
                    const index = state.serviceAreas.data.findIndex(item => item.id === updated.id);
                    if (index !== -1) {
                        state.serviceAreas.data[index] = updated;
                    } else {
                        // If not found, add it to the list
                        state.serviceAreas.data = [...state.serviceAreas.data, updated];
                    }
                }
                state.error = null;
            })
            .addCase(updateServiceArea.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to update service area";
            })
            
            // Delete Service Area
            .addCase(deleteServiceArea.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteServiceArea.fulfilled, (state, action) => {
                state.loading = false;
                // Remove the deleted service area from state
                const deletedId = action.payload?.id;
                if (deletedId) {
                    state.serviceAreas.data = state.serviceAreas.data.filter(item => item.id !== deletedId);
                    state.serviceAreas.totalCount = Math.max(0, state.serviceAreas.totalCount - 1);
                }
                state.error = null;
            })
            .addCase(deleteServiceArea.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to delete service area";
            })
            
            // Get Service Area by ID
            .addCase(getServiceAreaById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getServiceAreaById.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedServiceArea = action.payload;
                state.error = null;
            })
            .addCase(getServiceAreaById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to fetch service area";
            })
            
            // Get Provinces and Cities
            .addCase(getProvincesAndCities.fulfilled, (state, action) => {
                if (action.payload) {
                    state.provincesAndCities = {
                        provinces: action.payload.provinces || [],
                        cities: action.payload.cities || {},
                    };
                }
                state.error = null;
            })
            .addCase(getProvincesAndCities.rejected, (state, action) => {
                state.error = action.payload as string || "Failed to fetch provinces and cities";
            });
    },
});

// Export actions
export const { clearSelectedServiceArea, clearError, setLoading } = serviceAreaSlice.actions;

// Export reducer
export default serviceAreaSlice.reducer;
